import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Briefcase, Zap, ShieldCheck, Wallet, MapPin, Clock, Calendar,
  Truck, HardHat, Package, Sparkles, Boxes, Trees, PartyPopper, Hammer, Laptop, MoreHorizontal,
  ArrowRight, Star, CheckCircle2, Users, TrendingUp,
} from "lucide-react";
import { api, rupiah, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogoMark } from "@/components/Logo";

const ICONS = { Truck, HardHat, Package, Sparkles, Boxes, Trees, PartyPopper, Hammer, Laptop, MoreHorizontal };
const HERO_IMG = "https://images.unsplash.com/photo-1786076520546-7af524d42f4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxJbmRvbmVzaWFuJTIwd29ya2VyJTIwY291cmllciUyMHBhY2thZ2luZ3xlbnwwfHx8fDE3ODc4MTY1ODV8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/jobs?limit=6").then(({ data }) => setJobs(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const go = (path) => (user ? navigate(path) : navigate("/daftar"));

  return (
    <div className="bg-white text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={36} rounded="rounded-lg" />
            <span className="font-extrabold text-lg tracking-tight">ANDIL</span>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Button data-testid="landing-app-btn" onClick={() => navigate("/app")} className="bg-blue-600 hover:bg-blue-700 rounded-lg">Buka Aplikasi</Button>
            ) : (
              <>
                <Button data-testid="landing-login-btn" variant="ghost" onClick={() => navigate("/masuk")} className="font-semibold">Masuk</Button>
                <Button data-testid="landing-register-btn" onClick={() => navigate("/daftar")} className="bg-blue-600 hover:bg-blue-700 rounded-lg">Daftar</Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-semibold mb-5">
            <Zap size={15} /> Marketplace kerja fleksibel Indonesia
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Temukan pekerjaan.<br /><span className="text-blue-600">Berikan kesempatan.</span>
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-lg leading-relaxed">
            ANDIL mempertemukan orang yang membutuhkan bantuan dengan orang yang siap bekerja dan mendapatkan penghasilan.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-7">
            <Button data-testid="hero-find-btn" onClick={() => go("/cari")} className="bg-blue-600 hover:bg-blue-700 rounded-lg h-12 px-7 text-base">
              <Search size={18} className="mr-2" /> Cari Pekerjaan
            </Button>
            <Button data-testid="hero-post-btn" onClick={() => go("/buat")} variant="outline" className="rounded-lg h-12 px-7 text-base border-2 border-slate-200 hover:border-slate-300">
              <Briefcase size={18} className="mr-2" /> Posting Pekerjaan
            </Button>
          </div>
          <div className="flex items-center gap-6 mt-8 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={16} className="text-emerald-500" /> Gratis</span>
            <span className="inline-flex items-center gap-1.5"><Star size={16} className="text-amber-400 fill-amber-400" /> Sistem rating</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={16} className="text-blue-500" /> Lokal & terdekat</span>
          </div>
        </div>
        <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
          <img src={HERO_IMG} alt="Pekerja ANDIL" className="rounded-3xl w-full h-[420px] object-cover shadow-[0_20px_60px_rgba(15,23,42,0.18)]" />
          <div className="absolute -bottom-5 -left-3 bg-white rounded-2xl shadow-xl p-4 border border-slate-100 hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Wallet className="text-emerald-600" size={20} /></div>
              <div>
                <p className="text-xs text-slate-400">Bayaran instan</p>
                <p className="font-bold">Rp50.000 – Rp500.000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cara kerja */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center">Cara kerja ANDIL</h2>
          <p className="text-slate-500 text-center mt-2">Empat langkah sederhana untuk mulai.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {[
              { icon: Search, t: "Temukan", d: "Cari pekerjaan fleksibel di dekatmu." },
              { icon: Briefcase, t: "Lamar", d: "Kirim lamaran dengan satu ketukan." },
              { icon: HardHat, t: "Kerjakan", d: "Selesaikan pekerjaan sesuai kesepakatan." },
              { icon: Wallet, t: "Dapatkan penghasilan", d: "Terima bayaran & rating." },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4"><s.icon className="text-blue-600" size={22} /></div>
                <span className="text-xs font-bold text-blue-600">LANGKAH {i + 1}</span>
                <h3 className="font-bold text-lg mt-1">{s.t}</h3>
                <p className="text-slate-500 text-sm mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Jobs */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Zap className="text-amber-500 fill-amber-400" size={26} /> Quick Jobs</h2>
            <p className="text-slate-500 mt-1">Pekerjaan yang bisa kamu kerjakan.</p>
          </div>
          <Button data-testid="quickjobs-all-btn" variant="ghost" onClick={() => go("/cari")} className="text-blue-600 font-semibold">Lihat semua <ArrowRight size={16} className="ml-1" /></Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {jobs.map((j, i) => (
            <div key={j.id} data-testid={`landing-job-${j.id}`} onClick={() => go(`/pekerjaan/${j.id}`)}
              className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:border-slate-300 transition-all cursor-pointer animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
              <span className="inline-block bg-slate-100 text-slate-600 px-2.5 py-0.5 rounded-full text-xs font-medium mb-2">{j.category?.name}</span>
              <h3 className="font-bold text-lg">{j.title}</h3>
              <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-2"><MapPin size={15} className="text-blue-600" /> {j.city}{j.distance_km != null && ` · ${j.distance_km} km`}</div>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <span className="font-bold inline-flex items-center gap-1.5"><Wallet size={15} className="text-emerald-600" /> {rupiah(j.payment)}</span>
                <span className="text-slate-500 inline-flex items-center gap-1.5"><Clock size={15} /> {j.duration}</span>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100 text-sm">
                <span className="text-slate-400 inline-flex items-center gap-1"><Calendar size={14} /> {formatDate(j.date)}</span>
                <span className="text-blue-600 font-semibold">Lihat Detail →</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Kategori */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center">Kategori pekerjaan</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
            {cats.map((c) => {
              const Icon = ICONS[c.icon] || MoreHorizontal;
              return (
                <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => go("/cari")}
                  className="bg-white rounded-2xl p-5 border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all flex flex-col items-center gap-3 text-center">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center"><Icon className="text-blue-600" size={22} /></div>
                  <span className="font-semibold text-sm text-slate-700">{c.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-6">
        <div className="bg-blue-600 text-white rounded-3xl p-8">
          <TrendingUp size={32} className="mb-4" />
          <h3 className="text-2xl font-bold">Untuk Pekerja</h3>
          <p className="text-blue-100 mt-2">Temukan pekerjaan fleksibel sesuai waktu dan kemampuanmu.</p>
          <ul className="mt-5 space-y-3">
            {["Pekerjaan lokal & terdekat", "Bayaran jelas di awal", "Bangun reputasi lewat rating", "Kerja sesuai jadwalmu"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 size={18} className="text-blue-200" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="bg-slate-900 text-white rounded-3xl p-8">
          <Users size={32} className="mb-4" />
          <h3 className="text-2xl font-bold">Untuk Pemberi Kerja</h3>
          <p className="text-slate-300 mt-2">Temukan orang yang siap membantu pekerjaanmu.</p>
          <ul className="mt-5 space-y-3">
            {["Posting pekerjaan gratis", "Lihat rating & profil pelamar", "Kelola pelamar dengan mudah", "Chat langsung setelah diterima"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 size={18} className="text-slate-400" /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <ShieldCheck size={40} className="text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold">Dibangun atas kepercayaan</h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">Setiap pekerja dan pemberi kerja memiliki rating, ulasan, dan riwayat pekerjaan. Pilih dengan yakin berdasarkan reputasi nyata.</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-blue-600 rounded-3xl p-10 sm:p-14 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Siap mulai hari ini?</h2>
          <p className="text-blue-100 mt-3 text-lg">Gabung ANDIL dan temukan peluang di sekitarmu.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
            <Button data-testid="cta-register-btn" onClick={() => navigate("/daftar")} className="bg-white text-blue-700 hover:bg-blue-50 rounded-lg h-12 px-8 text-base font-bold">Daftar Sekarang</Button>
            <Button data-testid="cta-login-btn" onClick={() => navigate("/masuk")} variant="outline" className="rounded-lg h-12 px-8 text-base border-2 border-white/40 text-white hover:bg-white/10 bg-transparent">Masuk</Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-10">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <LogoMark size={32} rounded="rounded-md" />
            <span className="font-extrabold">ANDIL</span>
          </div>
          <p className="text-slate-400 text-sm">Temukan pekerjaan. Berikan kesempatan.</p>
          <p className="text-slate-400 text-sm">© 2026 ANDIL</p>
        </div>
      </footer>
    </div>
  );
}
